import { ActionIcon, ColorInput, Divider, Group, NumberInput, Paper, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { ProcessingTextMode, WebBotProviderData } from '@/API';
import { MarkdownEditor } from '@/Components/MarkdownEditor';

interface WebBotTextTabProps {
  form: UseFormReturnType<WebBotProviderData>;
}

export function WebBotTextTab({ form }: WebBotTextTabProps) {
  return (
    <Stack gap="md">
      <TextInput
        withAsterisk
        label="標題"
        {...form.getInputProps('text.title')}
      />
      <TextInput
        label="Bot名稱 (顯示在對話時的Icon旁邊)"
        placeholder="AI助手"
        {...form.getInputProps('text.botName')}
      />
      <TextInput
        label="輸入框提示文字"
        placeholder="預設 輸入訊息"
        {...form.getInputProps('text.inputPlaceholder')}
      />
      <TextInput
        label="回饋提示文字"
        placeholder="未設定則不顯示提示"
        {...form.getInputProps('text.feedbackNotificationText')}
      />
      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              思考中動態文字設定
            </Text>
            <Group gap="xs" align="center">
              <Text size="xs" c="dimmed">
                切換模式
              </Text>
              <SegmentedControl
                size="xs"
                data={[
                  { label: '順序', value: ProcessingTextMode.Sequence },
                  { label: '隨機', value: ProcessingTextMode.Random },
                ]}
                value={form.values.text?.processingTextMode ?? ProcessingTextMode.Sequence}
                onChange={(val) => form.setFieldValue('text.processingTextMode', val as ProcessingTextMode)}
              />
            </Group>
          </Group>

          <Divider style={{ opacity: 0.6 }} />

          <Stack gap="xs">
            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed">
                文字列表與停留秒數 (留空則僅顯示動畫)
              </Text>
              <ActionIcon
                variant="light"
                color="blue"
                size="sm"
                onClick={() => form.insertListItem('text.processingTexts', { text: '', seconds: 3 })}
              >
                <IconPlus size="0.9rem" />
              </ActionIcon>
            </Group>
            {form.values.text?.processingTexts?.map((item, index) => (
              <Group key={index} gap="xs" align="flex-end">
                <TextInput
                  placeholder="例如: 思考中..."
                  label={index === 0 ? '顯示文字' : undefined}
                  style={{ flex: 1 }}
                  {...form.getInputProps(`text.processingTexts.${index}.text`)}
                />
                <NumberInput
                  placeholder="秒"
                  label={index === 0 ? '停留秒數' : undefined}
                  min={0.5}
                  step={0.5}
                  decimalScale={1}
                  suffix=" 秒"
                  style={{ width: 110 }}
                  {...form.getInputProps(`text.processingTexts.${index}.seconds`)}
                  value={item.seconds ?? ''}
                />
                <ActionIcon
                  color="red"
                  variant="subtle"
                  mb={4}
                  onClick={() => form.removeListItem('text.processingTexts', index)}
                >
                  <IconTrash size="1rem" />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Paper>
      <Paper withBorder p="sm" radius="md">
        <Stack gap="xs">
          <MarkdownEditor
            label="底部提示文字 (支援 Markdown，可用於免責聲明、隱私權連結等)"
            placeholder="例如: [隱私權政策](https://...)"
            value={form.values.text?.footerText ?? ''}
            onChange={(val) => form.setFieldValue('text.footerText', val)}
          />
          <ColorInput
            label="底部提示文字顏色"
            placeholder="留空則使用預設灰階顏色"
            {...form.getInputProps('footer.textColor')}
            value={form.values.footer?.textColor ?? ''}
          />
        </Stack>
      </Paper>
      <Paper withBorder p="sm" radius="md">
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Text size="sm" fw={500}>
              預設問題設定
            </Text>
            <ActionIcon
              variant="light"
              color="blue"
              size="sm"
              onClick={() => form.insertListItem('text.defaultQuestions', '')}
            >
              <IconPlus size="0.9rem" />
            </ActionIcon>
          </Group>

          <Divider style={{ opacity: 0.6 }} />

          <Stack gap="xs">
            {form.values.text?.defaultQuestions?.map((_, index) => (
              <Group key={index} gap="xs">
                <TextInput
                  placeholder="輸入預設問題"
                  style={{ flex: 1 }}
                  {...form.getInputProps(`text.defaultQuestions.${index}`)}
                />
                <ActionIcon
                  color="red"
                  variant="subtle"
                  onClick={() => form.removeListItem('text.defaultQuestions', index)}
                >
                  <IconTrash size="1rem" />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
